// Centralized API / WebSocket endpoint configuration.
//
// Default behaviour is preserved: subdomain-aware URLs derived from the
// current hostname (e.g. `comptest1.icont.ai` → `comptest1.console.icont.ai`).
//
// Override per environment via Vite env vars (e.g. `.env.kktcedevlet`,
// loaded with `vite --mode kktcedevlet`):
//   VITE_TENANT       = kktcedevlet               # pins the subdomain
//   VITE_API_BASE_URL = https://kktcedevlet.console.icont.ai/api
//   VITE_WS_BASE_URL  = wss://websocket.payeda.net
//   VITE_WS_PATH      = kktce
//
// `VITE_TENANT` is the cleanest dev override: it both fixes the subdomain
// used in headers (X-Subdomain) and makes the URL defaults resolve to that
// tenant without needing to spell each base URL out.

// Recognised hostname bases. The first match wins; .icont.ai is checked
// first to preserve prior behaviour for existing deployments. Adding .icont.xyz
// lets the same dashboard build serve the test mirror domain without any
// per-host hardcoding elsewhere.
const SUPPORTED_BASES = ["icont.ai", "icont.xyz"] as const;

function deriveTenantContext(): { subdomain: string | null; consoleHost: string } {
  // Explicit tenant override (e.g. set by .env.kktcedevlet for `npm run dev:kktce`).
  const explicit = import.meta.env.VITE_TENANT as string | undefined;

  const hostname = window.location.hostname;
  for (const base of SUPPORTED_BASES) {
    if (hostname === base || hostname.endsWith("." + base)) {
      const parts = hostname.split(".");
      const baseParts = base.split(".");
      const sd = parts.slice(0, parts.length - baseParts.length).join(".") || null;
      return { subdomain: explicit ?? sd, consoleHost: `console.${base}` };
    }
  }
  // Unknown host (localhost etc.) → fall back to .icont.ai for default URLs.
  return { subdomain: explicit ?? null, consoleHost: "console.icont.ai" };
}

const _ctx = deriveTenantContext();
export const subdomain: string | null = _ctx.subdomain;
const consoleHost: string = _ctx.consoleHost;

const stripTrailingSlash = (s: string): string => s.replace(/\/+$/, "");

// HTTP API base (no trailing slash). Pass through `${apiBaseUrl}/some/path`.
export const apiBaseUrl: string = stripTrailingSlash(
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
    (subdomain
      ? `https://${subdomain}.${consoleHost}/api`
      : `https://${consoleHost}/api`)
);

// Tells the gateway this is an AGENT session. The chat widget signs in on a
// host whose cookies also reach this API, so a browser that has used the widget
// can send a client token alongside the agent's; without this header the
// gateway could pick the client one and the dashboard session would turn into
// the widget's. Must accompany every request that carries auth cookies.
export const SESSION_KIND_HEADER = { "X-Session-Kind": "agent" } as const;

// The reader's IANA time zone (e.g. "Europe/Istanbul"). Reports are built on
// the server, which runs in UTC; with this the gateway prints dates and times
// — and draws day boundaries — the way the person asking for them sees them.
const browserTimeZone: string | undefined = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
})();

/** Headers for a credentialed request to the gateway outside baseApi. */
export const gatewayHeaders = (): Record<string, string> => ({
  ...SESSION_KIND_HEADER,
  ...(browserTimeZone ? { "X-Timezone": browserTimeZone } : {}),
  ...(subdomain ? { "X-Subdomain": subdomain } : {}),
});

// WebSocket host for MSRP (the proxy that fronts scont).
//
// Default behaviour: derive the WS host from the current page's TLD so that
// .icont.ai pages talk to wss://websocket.icont.ai and .icont.xyz pages talk
// to wss://websocket.icont.xyz. This keeps each environment's WS traffic on
// its own branded host instead of the legacy payeda.net hub.
//
// VITE_WS_BASE_URL still wins for explicit overrides (e.g. demo builds).
// Unknown hosts (localhost, ngrok) fall back to wss://websocket.payeda.net
// so existing dev flows keep working.
function deriveWsBaseUrl(): string {
  for (const base of SUPPORTED_BASES) {
    const hostname = typeof window !== "undefined" ? window.location.hostname : "";
    if (hostname === base || hostname.endsWith("." + base)) {
      return `wss://websocket.${base}`;
    }
  }
  return "wss://websocket.payeda.net";
}

export const wsBaseUrl: string =
  (import.meta.env.VITE_WS_BASE_URL as string | undefined) ||
  deriveWsBaseUrl();

// WS path slug — "ws3" is prod scont. Demo stacks set their own via
// VITE_WS_PATH (e.g. "kktce" for kktcedevlet).
export const wsPath: string =
  (import.meta.env.VITE_WS_PATH as string | undefined) ||
  (subdomain === "domaintest" ? "demotest" : "ws3");

// Full MSRP WS URL used by the dashboard's MSRP connection manager.
export const msrpWsUrl: string = `${wsBaseUrl}/${wsPath}`;
